import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { toMessage } from "@/api/errors";
import { upsertApiKey } from "@/api/lookups";
import {
  API_KEY_PROVIDERS,
  type ApiKeyProvider,
  type ApiKeyResponse,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const schema = z.object({
  provider: z.enum(API_KEY_PROVIDERS),
  value: z.string().min(8).max(4096),
});

type FormValues = z.infer<typeof schema>;

export interface ApiKeyFormProps {
  open: boolean;
  existing: ApiKeyResponse[];
  onClose: () => void;
  afterSubmit: () => Promise<unknown> | void;
}

export function ApiKeyForm({
  open,
  existing,
  onClose,
  afterSubmit,
}: ApiKeyFormProps) {
  const { t } = useTranslation();
  const [pendingRotation, setPendingRotation] = useState<FormValues | null>(
    null,
  );
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { provider: API_KEY_PROVIDERS[0], value: "" },
  });
  const upsert = useMutation({
    mutationFn: (v: FormValues) => upsertApiKey(v),
    onSuccess: async () => {
      await afterSubmit();
      onClose();
    },
  });

  const existingProviders = new Set<ApiKeyProvider>(
    existing.map((k) => k.provider as ApiKeyProvider),
  );

  const onSubmit = (v: FormValues) => {
    if (existingProviders.has(v.provider)) {
      setPendingRotation(v);
      return;
    }
    upsert.mutate(v);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => (v ? undefined : onClose())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("adminApiKeys.upsertButton")}</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={form.handleSubmit(onSubmit)}
            data-testid="api-key-form"
          >
            <div>
              <Label htmlFor="api-provider">
                {t("adminApiKeys.fields.provider")}
              </Label>
              <select
                id="api-provider"
                className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                {...form.register("provider")}
              >
                {API_KEY_PROVIDERS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="api-value">
                {t("adminApiKeys.fields.value")}
              </Label>
              <Input
                id="api-value"
                type="password"
                autoComplete="off"
                {...form.register("value")}
              />
              {form.formState.errors.value !== undefined ? (
                <p className="text-xs text-destructive">
                  {t("adminApiKeys.errors.valueRequired")}
                </p>
              ) : null}
            </div>
            {upsert.isError ? (
              <p className="text-xs text-destructive" role="alert">
                {toMessage(upsert.error, t("adminApiKeys.errors.saveFailed"))}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={upsert.isPending}>
                {upsert.isPending ? t("common.saving") : t("common.save")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingRotation !== null}
        onOpenChange={(v) => (v ? undefined : setPendingRotation(null))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle data-testid="rotate-confirm-title">
              {t("adminApiKeys.rotateConfirm", {
                provider: pendingRotation?.provider ?? "",
              })}
            </DialogTitle>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setPendingRotation(null)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (pendingRotation !== null) upsert.mutate(pendingRotation);
                setPendingRotation(null);
              }}
              data-testid="rotate-confirm-button"
            >
              {t("common.yes")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
